/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, Snackbar, SnackbarCloseReason, TextField } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { EmpFunService, IDetalheEmpCreate } from '../../../shared/services/api/rh/EmpFun/EmpFunService';

export interface ConfirmationDialogRawProps {
  value: string;
  dados: IDetalheEmpCreate;
  open: boolean;
  onClose: (value?: string) => void;
}

interface IFormData {
  idempr: string;
  idfun: string;
  valor: string;
  historico: string;
  datalan: Date;
  periodo: string;
  parcela: string;
  idoperacao: string;
  vtotal: number;
}

export const DialogEmpFun: React.FC<ConfirmationDialogRawProps> = (props: ConfirmationDialogRawProps) => {

  const { onClose, value: valueProp, open, ...other } = props;
  const [value, setValue] = useState(valueProp);
  const navigate = useNavigate();

  const meses = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

  const [lista, setLista] = useState<IFormData[]>([]);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if (tms === 'success') {
      onClose();
      navigate('/empfun');
    }
  };

  const SetPeriodo = (mesano: string, inc: number) => {
    const mes = mesano.slice(0, -5); 
    const ano = parseInt(mesano.slice(-4));
    const indice = meses.indexOf(mes);
    const totalMeses = 12;
    const indiceTotal = indice + inc;
    const newAno = ano + Math.floor(indiceTotal / totalMeses);
    const newMes = meses[indiceTotal % totalMeses];
    return `${newMes}/${newAno}`;
  };

  useEffect(() => {
    if (props.dados && typeof props.dados === 'object') {
      setLista([]);
      const guuid = uuidv4();
      const valorparcela = Number(props.dados.valor) / Number(props.dados?.parcela);
      if (Number(props.dados?.parcela) > 1) {
        for (let i = 0; i < Number(props.dados?.parcela); i++) {
          const ops = (
            {
              idempr: uuidv4(),
              idfun: props.dados.idfun,
              valor: valorparcela.toLocaleString('pt-BR', { minimumFractionDigits: 4 }),
              vtotal: props.dados.valor,
              historico: props.dados.historico,
              datalan: props.dados.datalan,
              periodo: SetPeriodo(props.dados.periodo, i),
              parcela: `${i + 1}/${props.dados.parcela}`,
              idoperacao: guuid,
            });
          setLista((oldLista) => [...oldLista, ops]);
        }
      }
    }
  }, [open, props.dados]);

  useEffect(() => {
    if (!open) {
      setValue(valueProp);
    }
  }, [valueProp, open]);

  const handleCancel = () => {
    onClose();
  };

  const handleOk = () => {
    const updatedList = lista.map((listItem) => {
      return {
        idempr: listItem.idempr,
        idfun: listItem.idfun,
        valor: Number(formatValueNumber(listItem.valor)),
        historico: listItem.historico,
        datalan: listItem.datalan,
        periodo: listItem.periodo,
        parcela: listItem.parcela,
        idoperacao: listItem.idoperacao,
        vtotal: listItem.vtotal,
      };
    });
    const soma = updatedList.reduce((acc, listItem) => acc + listItem.valor, 0);
    if (soma.toFixed(2) === props.dados.valor.toString()) {
      EmpFunService
        .createparcelas(updatedList)
        .then((result) => {
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTms('success');
            setMsg('Lançamento Realizado com sucesso!');
            setOpenmsg(true);
          }
        });
    } else {
      alert('Valor Total não Confere');
    }
  };
  const handleInputChange = useCallback((id: string, valor: string) => {
    const updatedList = lista.map((listItem) => {
      if (listItem.idempr === id) {
        return {
          ...listItem,
          valor: valor,
        }; 
      } else {
        return listItem;
      }
    });
    setLista(updatedList);
  }, [lista]);

  const formatInputValue = (inputValue: string) => {
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.replace(/(\d)(\d{4})$/, '$1,$2');
    inputValue = inputValue.replace(/(?=(\d{3})+(\D))\B/g, '.');
    return inputValue;
  };

  const formatValueNumber = (Value: string) => {
    if (Value === undefined || Value === null) {
      return '';
    }
    Value = Value.replace(/\D/g, '');
    Value = Value.replace(/(\d)(\d{4})$/, '$1.$2');
    Value = Value.replace(/(?=(\d{3})+(\D))\B/g, '');
    return Value;
  };

  return (
    <Dialog
      sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: 520 } }}
      maxWidth="xs"
      open={open}
      {...other}
    >
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={900} onClose={handleCloseMsg}>
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>;
      <DialogTitle>Parcelamento de Vale</DialogTitle>
      <DialogContent dividers>
        <List sx={{ pt: 0 }}>
          {lista.map((listItem) => (
            <ListItem disableGutters key={listItem.idempr}>
              <TextField
                size='small'
                value={listItem.periodo}
                label="Período"
              />
              <TextField
                sx={{ marginLeft: 3 }}
                size='small'
                value={listItem.valor}
                onChange={(e) => {
                  const inputValue = formatInputValue(e.target.value);
                  e.target.value = inputValue;
                  handleInputChange(listItem.idempr, inputValue);
                }}
                label="Valor"
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel}>
          Cancelar
        </Button>
        <Button onClick={handleOk}>Confirmar</Button>
      </DialogActions>
    </Dialog>

  );
}; 